import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntityManager } from "@/components/admin/EntityManager";
import { Submissions } from "@/components/admin/Submissions";
import { SiteEditor } from "@/components/admin/site/SiteEditor";
import { ProductsManager } from "@/components/admin/ProductsManager";

const DATE_FIELDS = [
  { k: "title", label: "Title", required: true },
  { k: "date", label: "Date (e.g. 2026-11-14 or TBA)" },
  { k: "time", label: "Time" },
  { k: "capacity", label: "Capacity", type: "number" },
  { k: "status", label: "Status", options: ["TENTATIVE", "CONFIRMED", "FULL", "CANCELLED"] },
  { k: "notes", label: "Notes", area: true },
];

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try { const { data } = await api.post("/auth/login", { email, password }); onDone(data); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <div className="min-h-screen grid place-items-center px-5 grid-lines">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-sm clip-cut bg-[var(--ink-2)] border border-[var(--line)] p-8 space-y-5">
        <Logo />
        <h1 className="font-display text-4xl font-black uppercase pt-4">Staff Access</h1>
        <label className="block"><span className="field-label">Email</span>
          <input data-testid="admin-email-input" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="block"><span className="field-label">Password</span>
          <input data-testid="admin-password-input" type="password" required className="field" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        {err && <p data-testid="admin-login-error" className="text-sm text-red-400">{err}</p>}
        <button data-testid="admin-login-submit" className="w-full clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-[var(--bone)] transition-colors">Log in</button>
        <Link to="/" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--bone)]">← Back to site</Link>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(false); toast("Logged out"); };

  if (user === null) return <div className="min-h-screen grid place-items-center font-mono text-xs text-[var(--muted)]">LOADING…</div>;
  if (!user) return <Login onDone={setUser} />;

  return (
    <div data-testid="admin-panel" className="min-h-screen px-5 sm:px-8 lg:px-12 py-8">
      <header className="flex items-center justify-between border-b border-[var(--line)] pb-6">
        <Link to="/" data-testid="admin-home-link"><Logo /></Link>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline font-mono text-xs text-[var(--muted)]">{user.email}</span>
          <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-[var(--line)] px-4 py-2.5 hover:border-[var(--signal)]">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </header>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase mt-10">Command Panel</h1>
      <Tabs defaultValue="site" className="mt-8">
        <TabsList className="bg-[var(--ink-2)] border border-[var(--line)] rounded-none h-auto flex-wrap justify-start p-1">
          {[["site", "Website"], ["products", "Products"], ["dates", "Training Dates"], ["inquiries", "Inquiries"], ["repairs", "Repairs"], ["waitlist", "Waitlist"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}
              className="rounded-none font-mono text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[var(--signal)] data-[state=active]:text-[var(--ink)]">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="site"><SiteEditor /></TabsContent>
        <TabsContent value="products"><ProductsManager /></TabsContent>
        <TabsContent value="dates"><EntityManager path="/training-dates" fields={DATE_FIELDS} name="date" /></TabsContent>
        <TabsContent value="inquiries"><Submissions path="/inquiries" cols={["name", "email", "phone", "type", "subject", "message"]} /></TabsContent>
        <TabsContent value="repairs"><Submissions path="/repairs" cols={["name", "email", "phone", "brand", "model", "service", "budget", "details"]} /></TabsContent>
        <TabsContent value="waitlist"><Submissions path="/waitlist" cols={["name", "email", "phone", "experience", "team_size", "preferred_date"]} /></TabsContent>
      </Tabs>
    </div>
  );
}
